import { watch } from "vue"
import { createRouter, createWebHistory, START_LOCATION } from "vue-router"
import { splitLocation } from "./lib/experiment"
import { holdRoute, navPending, pageScroll } from "./lib/navhold"
import { dropPreviewToken } from "./lib/previewFlag"
import { track } from "./lib/tracking"
import { useBootstrapStore } from "./stores/bootstrap"

// Route-level code splitting per the perf plan: every page is a lazy chunk.
export const router = createRouter({
  history: createWebHistory(),
  scrollBehavior: pageScroll,
  routes: [
    { path: "/", component: () => import("./pages/HomePage.vue") },
    // meta.hold: wait for this route's critical data before committing the
    // navigation (see lib/navhold), mirroring the meta.layout convention below.
    {
      path: "/products/:handle",
      name: "product",
      meta: { hold: true },
      component: () => import("./pages/ProductPage.vue"),
    },
    // Shopify redirects the bare /products to the all-products collection.
    { path: "/products", redirect: "/collections/all" },
    { path: "/collections", component: () => import("./pages/CollectionsPage.vue") },
    { path: "/collections/:handle", component: () => import("./pages/CollectionPage.vue") },
    { path: "/search", component: () => import("./pages/SearchPage.vue") },
    { path: "/cart", component: () => import("./pages/CartPage.vue") },
    // The checkout funnel runs in its own chrome-free layout (see App.vue /
    // components/CheckoutLayout.vue) — no nav header, footer, cart drawer or popup,
    // the way Shopify's checkout drops the online-store theme.
    { path: "/checkout", component: () => import("./pages/CheckoutPage.vue"), meta: { layout: "checkout" } },
    { path: "/upsell", component: () => import("./pages/UpsellPage.vue"), meta: { layout: "checkout" } },
    { path: "/order/:token", component: () => import("./pages/OrderPage.vue"), meta: { layout: "checkout" } },
    // Shopify's canonical policy handles → Dikafy's short keys (the dashboard editor
    // + footer already use the short keys). Static paths rank above /policies/:handle.
    { path: "/policies/privacy-policy", redirect: "/policies/privacy" },
    { path: "/policies/refund-policy", redirect: "/policies/refund" },
    { path: "/policies/terms-of-service", redirect: "/policies/terms" },
    { path: "/policies/shipping-policy", redirect: "/policies/shipping" },
    { path: "/policies/contact-information", redirect: "/pages/contact" },
    { path: "/policies/:handle", component: () => import("./pages/PolicyPage.vue") },
    { path: "/pages/:handle", component: () => import("./pages/PageView.vue") },
    { path: "/blog", component: () => import("./pages/BlogPage.vue") },
    { path: "/blog/:handle", component: () => import("./pages/ArticlePage.vue") },
    // Shopify blog URLs (/blogs/:blog[/:article]) → Dikafy's single-blog routes.
    { path: "/blogs/:blog", redirect: "/blog" },
    { path: "/blogs/:blog/:article", redirect: to => `/blog/${to.params.article}` },
    { path: "/account", component: () => import("./pages/account/AccountPage.vue") },
    { path: "/account/login", component: () => import("./pages/account/LoginPage.vue") },
    { path: "/account/register", component: () => import("./pages/account/RegisterPage.vue") },
    { path: "/account/orders/:token", component: () => import("./pages/account/OrderDetailPage.vue") },
    { path: "/:pathMatch(.*)*", component: () => import("./pages/NotFound.vue") },
  ],
})

// A minted preview token leaves the URL before anything else sees the navigation —
// before the URL split below builds a redirect from it, and before the router's
// first commit writes the landing URL back into history (see dropPreviewToken).
router.beforeEach(dropPreviewToken)

/* URL split tests (docs/plans/ab-testing.md CS5). Decided before the page commits,
   so a variant-B visit never paints the page it is split away from. Registered
   BEFORE the navigation hold, so a split product page is not fetched only to be
   left.

   The split page's page_view is reported before the redirect: it is how a B visit
   enters the exposed set by the same door as a control one (D3), and the report's
   sample-ratio check is what would say if it did not. Back never lands on the
   split page: its navigation never committed, so it is not in history.

   A first visit has no cached bootstrap to read the tests from, so the first
   navigation waits for it — index.html preloads /bootstrap beside the entry
   script, so by the time the page chunk has arrived the answer usually has too.
   Bounded, and backed by a re-check when the payload lands or revalidates: a
   test started after this browser cached the bootstrap still splits the visit on
   the next frame, rather than showing B's shopper control for the rest of the
   visit. */
router.beforeResolve(async (to, from) => {
  const bootstrap = useBootstrapStore()
  if (from === START_LOCATION) {
    if (!bootstrap.payload && !bootstrap.loaded) {
      await new Promise<void>(done => {
        const stop = watch(() => bootstrap.loaded, () => (stop(), done()))
        setTimeout(done, 2000)
      })
    }
    watch(
      () => bootstrap.payload?.experiments,
      exps => {
        const next = splitLocation(router.currentRoute.value, exps)
        if (next) void router.replace(next)
      },
    )
  }
  const next = splitLocation(to, bootstrap.payload?.experiments)
  if (next) track({ name: "page_view", path: to.fullPath })
  return next
})

/* Remix/Hydrogen model: keep the page the shopper is looking at — content, scroll
   position and all — until the next page's critical data is in the cache, then swap
   atomically. By the time beforeResolve runs, vue-router has already resolved the
   target route's lazy chunk (extractComponentsGuards is an earlier queue step), so
   this only ever waits on the network round trip the page was going to make anyway.
   A superseded navigation is discarded by vue-router itself
   (checkCanceledNavigationAndReject runs after every guard batch), so a third click
   never renders the second product. */
router.beforeResolve(async (to, from) => {
  // The very first navigation paints index.html's shell skeleton instead. Holding
  // there would only delay first paint behind a blank screen for no gain.
  if (from === START_LOCATION || !to.meta.hold) return
  await holdRoute(to)
})

// Clear the pending indicator on every outcome — committed, redirected, or
// cancelled by a faster third click.
router.afterEach(() => {
  navPending.value = false
})
